import * as Sentry from '@sentry/nextjs';
import { nanoid } from 'nanoid';
import { type FC, type PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

import { SOCKET_SERVICE_URL } from '@/configuration/client';
import getSessionId from '@/helpers/getSessionId';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import logger from '@/providers/Logging';
import TeamsContext from '@/providers/Teams/TeamsContext';
import getSocketToken from '@/tsClient/getSocketToken';
import joinSocketChannels from '@/tsClient/joinSocketChannels';

import SocketContext, { type SocketMessage, SocketMessageType } from './SocketContext';

export { default as isInboxUpdatedMessageData } from './isInboxUpdatedMessageData';

const socket = io(SOCKET_SERVICE_URL);

type SocketListener = {
  id: string;
  messageType: string;
  fn: (data: unknown) => unknown;
  ignoreSameSessionEvents?: boolean;
};

const isMessageFromSameSession = (message: SocketMessage): boolean => message.sessionId === getSessionId();

const SocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const [listeners, setListeners] = useState<SocketListener[]>([]);

  const [user] = useUser();
  const { creatorId } = useContext(TeamsContext);

  const { data: token, getData: getTokenFn } = useNoodleApi(getSocketToken);
  const { getData: joinChannelFn } = useNoodleApi(joinSocketChannels);

  const addListener = useCallback(
    ({
      fn,
      messageType,
      ignoreSameSessionEvents,
    }: {
      fn: (data: unknown) => unknown;
      messageType: SocketMessageType;
      ignoreSameSessionEvents?: boolean;
    }): string => {
      const listenerId = nanoid();
      setListeners((prev) => [
        ...prev,
        {
          fn,
          id: listenerId,
          ignoreSameSessionEvents,
          messageType,
        },
      ]);
      return listenerId;
    },
    [],
  );

  const removeListener = useCallback((id: string): void => setListeners((prev) => prev.filter((l) => l.id !== id)), []);

  useEffect(() => {
    if (user?.id) {
      getTokenFn({ creatorId });
    }
  }, [creatorId, user, getTokenFn]);

  useEffect(() => {
    if (token) {
      socket.emit('login', token);
    }
  }, [token]);

  useEffect(() => {
    socket.on('error', (data) => {
      logger.error('Socket error', data);
      let err: Error & { data?: unknown };
      if (!(data.error instanceof Error)) {
        err = new Error(data.error?.message || 'Unknown error');
        err.name = typeof data?.error?.name === 'string' ? data.error.name : 'SocketError';
        err.data = data; // keep original object for debugging
      } else {
        err = data.error;
      }
      Sentry.captureException(err);
    });
  }, []);

  useEffect(() => {
    socket.removeAllListeners('message');
    socket.on('message', (message: SocketMessage) => {
      logger.info('heard socket message', { messageType: message.type, sessionId: message.sessionId });
      listeners
        .filter(
          (l) => l.messageType === message.type && (!l.ignoreSameSessionEvents || !isMessageFromSameSession(message)),
        )
        .forEach((listener) => listener.fn(message.data));
    });
    return () => {
      socket.removeAllListeners('message');
    };
  }, [listeners]);

  const joinChannels = useCallback(
    async (channelIds: string[]) => {
      await joinChannelFn({ channels: channelIds });
    },
    [joinChannelFn],
  );

  return (
    <SocketContext.Provider
      value={{
        addListener,
        isInitialized: Boolean(token),
        joinChannels,
        removeListener,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;

export { SocketMessageType };
